/**
 * DuviCoreAdminKit — the platform-admin's app-local VIEW helpers.
 *
 * These are the small screen-level building blocks the drawn-down
 * platform-admin prototype (duvi-design/exports/surfaces/platform-admin.zip →
 * screens.jsx) composes inline that are NOT themselves shared
 * @duvi-agency/ui components: the custom glyphs the DS Icon set omits, the
 * mono chip / tag chip, the empty + skeleton states, the JSON diff block, the
 * markdown editor gutter + renderer, a menu popover that delegates trigger,
 * positioning, portaling and focus-management to @duvi-agency/ui's shared
 * dropdown menu `trigger` mode (Story STY-700 / STY-823), a testid-bearing
 * DataTable clone (the DS DataTable is presentational and can't emit per-row
 * testids the e2e contract needs), the filter multi-select, and the toast
 * bus.
 *
 * Everything visual here is composed from the DS token utilities
 * (`bg-surface`, `text-text-muted`, `text-caption`, …) exactly as the export
 * does — no bespoke colours. Purely presentational; behaviour stays in the
 * page hooks.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";
import {
  DuviSharedAdminIcon as Icon,
  DuviSharedAdminCheckbox as Checkbox,
  DuviSharedAdminDropdownMenu as DropdownMenu,
  DuviSharedAdminToast as Toast,
  DuviSharedAdminToastStack as ToastStack,
  type DuviSharedAdminToastVariant,
} from "@duvi-agency/ui";

/* ============================== CUSTOM GLYPHS ==============================
   Drawn via the DS Icon's `children` slot — the shapes the named DS icon set
   doesn't include (ported from screens.jsx). */
export const DuviCoreAdminShieldOff = ({
  size = 20,
  className,
}: {
  size?: number;
  className?: string;
}) => (
  <Icon size={size} className={className}>
    <path d="M19.7 14a7.94 7.94 0 0 0 .3-2V5l-8-3-3.16 1.19 M4.73 4.73 4 5v7c0 6 8 10 8 10a20.3 20.3 0 0 0 5.62-4.38 M1 1l22 22" />
  </Icon>
);
export const DuviCoreAdminShieldCheck = ({
  size = 16,
  className,
}: {
  size?: number;
  className?: string;
}) => (
  <Icon size={size} className={className}>
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z M9 12l2 2 4-4" />
  </Icon>
);
export const DuviCoreAdminSystemIcon = ({
  size = 16,
  className,
}: {
  size?: number;
  className?: string;
}) => (
  <Icon size={size} className={className}>
    <path d="M4 17V7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v10 M2 17h20 M8 21h8" />
  </Icon>
);
export const DuviCoreAdminTagIcon = ({
  size = 14,
  className,
}: {
  size?: number;
  className?: string;
}) => (
  <Icon size={size} className={className}>
    <path d="M20.59 13.41 13.42 20.6a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z M7 7h.01" />
  </Icon>
);

/* ============================== SMALL BITS ============================== */
export const DuviCoreAdminSkel = ({
  className = "",
}: {
  className?: string;
}) => (
  <div
    className={["animate-pulse bg-surface-sunken rounded", className]
      .filter(Boolean)
      .join(" ")}
  />
);

export const DuviCoreAdminMonoChip = ({
  children,
}: {
  children: ReactNode;
}) => (
  <span className="font-mono text-caption bg-surface-sunken text-text-secondary px-[7px] py-0.5 rounded whitespace-nowrap">
    {children}
  </span>
);

export function DuviCoreAdminChip({
  active,
  onClick,
  children,
}: {
  active?: boolean;
  onClick?: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "inline-flex items-center gap-[5px] text-caption font-semibold px-[9px] py-[3px] rounded-full leading-[1.4] cursor-pointer transition-colors",
        active
          ? "bg-primary text-primary-foreground"
          : "bg-transparent border border-border-strong text-text-secondary hover:bg-surface-hover",
      ].join(" ")}
    >
      {children}
    </button>
  );
}

export const DUVI_CORE_ADMIN_SECTION_LABEL =
  "text-caption font-semibold uppercase tracking-wide text-text-muted";

export function DuviCoreAdminEmptyState({
  icon,
  title,
  desc,
  action,
  testId,
}: {
  icon: ReactNode;
  title: ReactNode;
  desc?: ReactNode;
  action?: ReactNode;
  testId?: string;
}) {
  return (
    <div
      className="flex flex-col items-center text-center py-14 px-6"
      data-testid={testId}
    >
      <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-surface-sunken text-text-muted mb-3">
        {icon}
      </span>
      <div className="text-h4 font-semibold text-text-primary">{title}</div>
      {desc && (
        <div className="text-body-sm text-text-secondary mt-1 max-w-sm">
          {desc}
        </div>
      )}
      {action && <div className="mt-3">{action}</div>}
    </div>
  );
}

export function DuviCoreAdminTableSkeleton({
  rows = 6,
  cols = 5,
}: {
  rows?: number;
  cols?: number;
}) {
  return (
    <div className="px-4 py-2">
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex gap-4 py-[13px] border-b border-border">
          {Array.from({ length: cols }).map((_, c) => (
            <DuviCoreAdminSkel
              key={c}
              className={[
                "h-3.5 rounded-md",
                c === 1 ? "flex-[3]" : "flex-1",
              ].join(" ")}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

export function DuviCoreAdminJsonBlock({
  label,
  tint,
  data,
}: {
  label: string;
  tint: "error" | "success";
  data?: Record<string, unknown>;
}) {
  const empty = !data || Object.keys(data).length === 0;
  const border = tint === "error" ? "border-error" : "border-success";
  const bg =
    tint === "error"
      ? "bg-error-subtle text-error"
      : "bg-success-subtle text-success";
  return (
    <div className={["border rounded-md overflow-hidden", border].join(" ")}>
      <div
        className={["px-2.5 py-1.5 text-caption font-semibold", bg].join(" ")}
      >
        {label}
      </div>
      <pre className="m-0 px-3 py-2.5 font-mono text-[11.5px] leading-relaxed text-text-secondary whitespace-pre-wrap break-words">
        {empty ? "∅ (none)" : JSON.stringify(data, null, 2)}
      </pre>
    </div>
  );
}

export function DuviCoreAdminLineNumberedTextarea({
  value,
  onChange,
  placeholder,
  rows = 16,
  id,
  testId,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  rows?: number;
  id?: string;
  testId?: string;
}) {
  const taRef = useRef<HTMLTextAreaElement>(null);
  const gutRef = useRef<HTMLDivElement>(null);
  const lines = (value || "").split("\n").length || 1;
  const sync = () => {
    if (gutRef.current && taRef.current)
      gutRef.current.scrollTop = taRef.current.scrollTop;
  };
  return (
    <div className="flex border border-border-strong rounded-md overflow-hidden bg-surface">
      <div
        ref={gutRef}
        className="py-3 bg-surface-sunken text-text-muted font-mono text-[12.5px] leading-[22px] text-right select-none overflow-hidden flex-none w-11"
      >
        {Array.from({ length: lines }).map((_, i) => (
          <div key={i} className="px-2.5">
            {i + 1}
          </div>
        ))}
      </div>
      <textarea
        ref={taRef}
        id={id}
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onScroll={sync}
        placeholder={placeholder}
        rows={rows}
        spellCheck={false}
        className="flex-1 border-none outline-none resize-y py-3 px-3.5 font-mono text-[13px] leading-[22px] bg-transparent text-text-primary"
      />
    </div>
  );
}

/* ============================== MENU POPOVER ==============================
   Thin controlled wrapper around @duvi-agency/ui's DuviSharedAdminDropdownMenu
   in `trigger` mode (Story STY-700 / fixed here by STY-823). All positioning,
   portaling (createPortal → document.body), scroll/resize repositioning,
   outside-click dismissal, Escape handling and focus management live in the
   shared component now — this wrapper owns only the open/close state so a
   raw-JSX menu body (custom testids, disabled rows — content the shared
   `items` prop can't express) can close itself via the `close` callback
   passed to render-prop children. Do not reintroduce hand-rolled positioning
   here: a wrapper that renders its panel inside the local DOM subtree is
   exactly the STY-700/STY-823 bug — the panel must portal to `document.body`
   to escape any ancestor `overflow` clipping. */
export function DuviCoreAdminMenuPopover({
  trigger,
  children,
  align = "right",
  closeOnInsideClick = true,
  panelClassName,
}: {
  trigger: ReactElement;
  children: ReactNode | ((close: () => void) => ReactNode);
  align?: "left" | "right";
  closeOnInsideClick?: boolean;
  /** Extra classes merged onto the portaled panel surface. */
  panelClassName?: string;
}) {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);

  return (
    <DropdownMenu
      trigger={trigger}
      align={align}
      open={open}
      onOpenChange={setOpen}
      className={panelClassName}
    >
      <div onClick={closeOnInsideClick ? close : undefined}>
        {typeof children === "function" ? children(close) : children}
      </div>
    </DropdownMenu>
  );
}

export function duviCoreAdminMenuItemCls(danger?: boolean, disabled?: boolean) {
  return [
    "flex items-center gap-2.5 w-full text-left border-none bg-transparent cursor-pointer font-sans text-body px-2.5 py-2 rounded-sm transition-colors",
    disabled
      ? "opacity-40 cursor-not-allowed"
      : danger
        ? "text-error hover:bg-error-subtle"
        : "text-text-primary hover:bg-surface-hover",
  ]
    .filter(Boolean)
    .join(" ");
}

/* ============================== MULTI-SELECT ============================== */
export type DuviCoreAdminMultiSelectOption =
  | string
  | { value: string; label: string };

export function DuviCoreAdminMultiSelect({
  label,
  options,
  value,
  onChange,
  width = 150,
  testId,
}: {
  label: string;
  options: DuviCoreAdminMultiSelectOption[];
  value: string[];
  onChange: (next: string[]) => void;
  width?: number | string;
  testId?: string;
}) {
  const widthStyle: React.CSSProperties = { width };
  const opts = options.map((o) =>
    typeof o === "string" ? { value: o, label: o } : o,
  );
  return (
    <DuviCoreAdminMenuPopover
      align="left"
      closeOnInsideClick={false}
      panelClassName="min-w-0"
      trigger={
        <button
          type="button"
          data-testid={testId}
          style={widthStyle}
          className="w-full text-left flex items-center justify-between gap-2 font-sans text-body text-text-primary bg-surface border border-input rounded pl-3 pr-3 h-10 cursor-pointer transition-[border-color,box-shadow] hover:border-border-strong"
        >
          <span className="overflow-hidden text-ellipsis whitespace-nowrap">
            {value.length ? `${label}: ${value.length}` : label}
          </span>
          <Icon name="chevronDown" size={14} className="text-text-muted" />
        </button>
      }
    >
      <div style={widthStyle}>
        {opts.map((o) => (
          <label
            key={o.value}
            className="flex items-center gap-2.5 px-2.5 py-2 rounded-sm cursor-pointer hover:bg-surface-hover"
          >
            <Checkbox
              checked={value.includes(o.value)}
              onChange={() =>
                onChange(
                  value.includes(o.value)
                    ? value.filter((x) => x !== o.value)
                    : [...value, o.value],
                )
              }
            />
            <span className="text-body">{o.label}</span>
          </label>
        ))}
      </div>
    </DuviCoreAdminMenuPopover>
  );
}

/* ============================== TABLE ==============================
   A testid-bearing clone of the DS DataTable's appearance (same TH/TD DS
   classes as duvi-shared-admin-data-table-view) — needed because the shared
   DataTable is presentational and can't emit the per-row `data-testid` the e2e
   contract requires, nor a row-click. Column `render(row)` cells are where the
   actor-avatar / colour-coded action Badge composition lives. */
export interface DuviCoreAdminTableColumn<Row> {
  key: string;
  header: ReactNode;
  align?: "left" | "right" | "center";
  width?: number | string;
  render?: (row: Row) => ReactNode;
}

const TH =
  "text-left text-caption font-semibold uppercase tracking-wide text-text-muted px-4 py-3 border-b border-border whitespace-nowrap";
const TD = "px-4 py-3.5 border-b border-border text-text-primary align-middle";

export function DuviCoreAdminTable<Row extends Record<string, unknown>>({
  columns,
  data,
  rowKey = "id",
  onRowClick,
  rowTestId,
  rowClassName,
  className,
}: {
  columns: DuviCoreAdminTableColumn<Row>[];
  data: Row[];
  rowKey?: string;
  onRowClick?: (row: Row) => void;
  rowTestId?: string;
  rowClassName?: (row: Row) => string | undefined;
  className?: string;
}) {
  const clickable = Boolean(onRowClick);
  return (
    <div
      className={["w-full overflow-x-auto", className]
        .filter(Boolean)
        .join(" ")}
    >
      <table className="w-full border-collapse text-body-sm">
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                className={TH}
                style={{ width: col.width, textAlign: col.align || "left" }}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row) => {
            const k = String(row[rowKey]);
            const handleRowClick = onRowClick
              ? (e: React.MouseEvent) => {
                  if (
                    (e.target as HTMLElement).closest("a, button, input, label")
                  )
                    return;
                  onRowClick(row);
                }
              : undefined;
            return (
              <tr
                key={k}
                data-testid={rowTestId}
                onClick={handleRowClick}
                className={[
                  "transition-colors hover:bg-surface-hover [&:last-child>td]:border-0",
                  clickable ? "cursor-pointer" : "",
                  rowClassName?.(row) ?? "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={TD}
                    style={{ textAlign: col.align || "left" }}
                  >
                    {col.render ? col.render(row) : (row[col.key] as ReactNode)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ============================== TOAST BUS ============================== */
type PushToast = (
  variant: DuviSharedAdminToastVariant,
  title: string,
  description?: string,
) => void;

interface ToastRecord {
  id: string;
  variant: DuviSharedAdminToastVariant;
  title: string;
  description?: string;
}

const ToastCtx = createContext<PushToast>(() => {});

export function useDuviCoreAdminToast(): PushToast {
  return useContext(ToastCtx);
}

export function DuviCoreAdminToastProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);
  const push = useCallback<PushToast>((variant, title, description) => {
    const id = Math.random().toString(36).slice(2);
    setToasts((t) => [...t, { id, variant, title, description }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 4200);
  }, []);
  return (
    <ToastCtx.Provider value={push}>
      {children}
      <ToastStack>
        {toasts.map((t) => (
          <Toast
            key={t.id}
            variant={t.variant}
            title={t.title}
            description={t.description}
            onClose={() => setToasts((s) => s.filter((x) => x.id !== t.id))}
          />
        ))}
      </ToastStack>
    </ToastCtx.Provider>
  );
}
